import decode from 'jwt-decode';
import { CNavTitle } from '@coreui/react';

/** Roles that can be assigned from Team → Register / Edit (not admin). */
export const ASSIGNABLE_TEAM_ROLES = [
  { value: 'assistant_admin', label: 'Assistant Admin' },
  { value: 'order_management_specialist', label: 'Order Management Specialist' },
  { value: 'order_management_associate', label: 'Order Management Associate' },
  { value: 'delivery_associate', label: 'Delivery Associate' },
  { value: 'e_commerce_admin', label: 'E-Commerce Administrator' },
  { value: 'content_manager', label: 'Content Manager' },
];

export const ALL_STAFF_ROLES = [
  'admin',
  ...ASSIGNABLE_TEAM_ROLES.map((r) => r.value),
];

const ORDER_DESK_ROLES = new Set([
  'order_management_specialist',
  'order_management_associate',
  'delivery_associate',
]);

/** Paths Content Manager may open (not full /store — excludes products/collections/filters). */
const CONTENT_MANAGER_PATH_PREFIXES = [
  '/cv-templates',
  '/customize',
  '/navigations',
  '/files',
  '/store/blog',
  '/store/categories',
  '/store/pages',
  '/store/photo-requirements-pages',
];

/** Human-readable summaries for the Team tab (admin reference). */
export const ROLE_DESCRIPTIONS = {
  admin:
    'Full backend access: dashboard, all orders (including Abandoned Checkouts), settings & SEO, customize, areas, storefront, files, feedbacks, preferences, and Team management.',
  assistant_admin:
    'Same as Admin except Team management. Can manage orders (including Abandoned Checkouts), settings & SEO, customize, areas, storefront, files, feedbacks, and preferences.',
  order_management_specialist:
    'Orders only (last 7 days). Sees pricing and customer spend stats. Can cancel orders, assign delivery, and download order information templates. No Abandoned Checkouts.',
  order_management_associate:
    'Orders only (last 7 days). Pricing and invoices are hidden. Can cancel orders, assign delivery and download order information templates. No Abandoned Checkouts.',
  delivery_associate:
    'Orders assigned to them only. Unfulfilled assignments stay visible; fulfilled orders remain visible for 48 hours after delivery. No pricing. Can complete delivery in Order Tracking for their own assignments. No Abandoned Checkouts.',
  e_commerce_admin:
    'Storefront only: products, collections, pages, blogs, filters, and photo requirements pages. No orders, settings, customize, or team.',
  content_manager:
    'Manages website content: CV Templates, Blogs, Customize, Photo Requirement Pages, Policy Pages, Navigation, and Files. No orders, products, settings, areas, or Team management.',
};

export function getRoleDescription(role) {
  return ROLE_DESCRIPTIONS[role] || '';
}

export function getRoleFromToken() {
  const token = localStorage.getItem('mern_admin_dashboard');
  if (!token) return null;
  try {
    const { role, exp } = decode(token);
    if (exp < new Date().getTime() / 1000) return null;
    return role;
  } catch {
    return null;
  }
}

export function normalizePath(path) {
  if (!path) return '';
  const base = path.split('?')[0].replace(/\/+$/, '') || '/';
  return base.startsWith('/') ? base : `/${base}`;
}

function pathMatchesPrefix(path, prefix) {
  return path === prefix || path.startsWith(`${prefix}/`);
}

function canContentManagerAccessPath(path) {
  return CONTENT_MANAGER_PATH_PREFIXES.some((prefix) =>
    pathMatchesPrefix(path, prefix)
  );
}

function canCustomize(role) {
  return (
    role === 'admin' ||
    role === 'assistant_admin' ||
    role === 'content_manager'
  );
}

export function getDefaultHomePath(role) {
  if (ORDER_DESK_ROLES.has(role)) return '/orders/all';
  if (role === 'e_commerce_admin') return '/store/products';
  if (role === 'content_manager') return '/cv-templates';
  return '/dashboard';
}

/**
 * Whether a logged-in role may open this URL in the admin app.
 * Matches backend requireRole rules; API still enforces on every request.
 */
export function canAccessPath(pathname, role) {
  if (!role) return true;
  const path = normalizePath(pathname);

  if (path.startsWith('/user/')) return true;

  // Abandoned Checkouts: Admin & Assistant Admin only (nav + direct URL).
  if (pathMatchesPrefix(path, '/orders/abandoned-checkouts')) {
    return role === 'admin' || role === 'assistant_admin';
  }

  // Courier Services admin page — not for delivery associates.
  if (pathMatchesPrefix(path, '/orders/courier-services')) {
    return (
      role === 'admin' ||
      role === 'assistant_admin' ||
      role === 'order_management_specialist' ||
      role === 'order_management_associate'
    );
  }

  if (path.startsWith('/customize')) {
    return canCustomize(role);
  }

  // Driver GPS page — order desk + admins (API still enforces assignment on ping).
  if (pathMatchesPrefix(path, '/delivering')) {
    return (
      role === 'admin' ||
      role === 'assistant_admin' ||
      ORDER_DESK_ROLES.has(role)
    );
  }

  if (role === 'admin') return true;

  if (role === 'assistant_admin') {
    return !path.startsWith('/team');
  }

  if (ORDER_DESK_ROLES.has(role)) {
    return path.startsWith('/orders');
  }

  if (role === 'e_commerce_admin') {
    return path.startsWith('/store');
  }

  if (role === 'content_manager') {
    return canContentManagerAccessPath(path);
  }

  return false;
}

function navItemToPath(item) {
  const to = item?.to;
  if (!to) return null;
  return normalizePath(to);
}

function isNavTitle(item) {
  return item?.component === CNavTitle;
}

function filterNavItem(item, role) {
  if (isNavTitle(item)) {
    return item;
  }

  if (item.items?.length) {
    const items = item.items
      .map((child) => filterNavItem(child, role))
      .filter(Boolean);
    if (items.length === 0) return null;
    return { ...item, items };
  }

  const path = navItemToPath(item);
  if (!path) return item;

  if (path === '/customize') {
    return canCustomize(role) ? item : null;
  }

  return canAccessPath(path, role) ? item : null;
}

/** Remove section titles with no visible nav items before the next title. */
function stripEmptyNavTitles(items) {
  const result = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (!isNavTitle(item)) {
      result.push(item);
      continue;
    }
    let j = i + 1;
    while (j < items.length && isNavTitle(items[j])) {
      j += 1;
    }
    if (j < items.length) {
      result.push(item);
    }
  }
  return result;
}

export function filterNavigationForRole(navItems, role) {
  if (!role || role === 'admin') {
    return navItems;
  }
  const filtered = (navItems || [])
    .map((item) => filterNavItem(item, role))
    .filter(Boolean);
  return stripEmptyNavTitles(filtered);
}
